import {
    type ColorScheme,
    useColorScheme,
} from '@precon-tools-suite-by-shared/vue-color-schemes';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { type WritableComputedRef, computed, ref, watch } from 'vue';
import { range } from 'lodash';
import type { usePrimeVue } from 'primevue/config';
import { useReceiverUserConfigStore } from '@precon-tools-suite-by-shared/primevue-receivers';

export type InputStyle = 'outlined' | 'filled';
export type MenuMode =
    | 'static'
    | 'overlay'
    | 'slim'
    | 'slim-plus'
    | 'horizontal'
    | 'reveal'
    | 'drawer';
export type TextDirection = 'ltr' | 'rtl';
export type UserConfigItem =
    | 'menu-mode'
    | 'ripple'
    | 'scale'
    | 'input-style'
    | 'color-scheme'
    | 'transparent-topbar'
    | 'locale'
    | 'dismissable-mask'
    | 'close-on-escape';
export type ConfigTogglePosition = 'floating' | 'topbar' | 'none';

export interface AppConfigOptions {
    textDirection?: TextDirection;
    defaultRipple?: boolean;
    defaultInputStyle?: InputStyle;
    defaultMenuMode?: MenuMode;
    menuModes?: MenuMode[];
    userConfigItems?: UserConfigItem[];
    defaultSidebarAnchored?: boolean;
    minScale?: number;
    maxScale?: number;
    scaleStep?: number;
    defaultScale?: number;
    hideSidebarTimeout?: number;
    defaultTransparentTopbar?: boolean;
    defaultConfigTogglePosition?: ConfigTogglePosition;
    defaultDismissableMask?: boolean;
    defaultCloseOnEscape?: boolean;
}

export const useAppConfigStore = defineStore('freya-app-config-store', () => {
    const receiverConfigStore = useReceiverUserConfigStore();

    const textDirection = ref<TextDirection>('ltr');
    const defaultRipple = ref<boolean>(true);
    const defaultInputStyle = ref<InputStyle>('outlined');
    const defaultMenuMode = ref<MenuMode>('horizontal');
    const defaultSidebarAnchored = ref<boolean>(false);
    const minScale = ref<number>(11);
    const maxScale = ref<number>(17);
    const defaultScale = ref<number>(14);
    const scaleStep = ref<number>(1);
    const defaultTransparentTopbar = ref<boolean>(false);
    const menuModes = ref<MenuMode[]>([
        'static',
        'overlay',
        'slim',
        'slim-plus',
        'horizontal',
        'reveal',
        'drawer',
    ]);
    const userConfigItems = ref<UserConfigItem[]>([
        'locale',
        'menu-mode',
        'transparent-topbar',
        'input-style',
        'scale',
        'color-scheme',
        'ripple',
        'dismissable-mask',
        'close-on-escape',
    ]);
    const defaultConfigTogglePosition = ref<ConfigTogglePosition>('topbar');
    const hideSidebarTimeout = ref<number>(300);

    function configure(options: AppConfigOptions | null | undefined) {
        options ??= {};

        if (options.textDirection) textDirection.value = options.textDirection;
        if (options.defaultRipple !== undefined)
            defaultRipple.value = options.defaultRipple;
        if (options.defaultInputStyle)
            defaultInputStyle.value = options.defaultInputStyle;
        if (options.defaultMenuMode)
            defaultMenuMode.value = options.defaultMenuMode;
        if (options.defaultSidebarAnchored)
            defaultSidebarAnchored.value = options.defaultSidebarAnchored;
        if (typeof options.minScale === 'number')
            minScale.value = options.minScale;
        if (typeof options.maxScale === 'number')
            maxScale.value = options.maxScale;
        if (typeof options.scaleStep === 'number' && options.scaleStep > 0)
            scaleStep.value = options.scaleStep;
        if (typeof options.defaultScale === 'number')
            defaultScale.value = options.defaultScale;
        if (options.defaultTransparentTopbar !== undefined)
            defaultTransparentTopbar.value = !!options.defaultTransparentTopbar;
        if (options.menuModes) menuModes.value = options.menuModes;
        if (options.userConfigItems)
            userConfigItems.value = options.userConfigItems;
        if (options.defaultConfigTogglePosition)
            defaultConfigTogglePosition.value =
                options.defaultConfigTogglePosition;
        if (typeof options.hideSidebarTimeout === 'number')
            hideSidebarTimeout.value = options.hideSidebarTimeout;
        receiverConfigStore.configure(options);
    }

    const ltr = computed({
        get: () => textDirection.value === 'ltr',
        set: (newValue) => {
            if (newValue) {
                textDirection.value = 'ltr';
            } else {
                textDirection.value = 'rtl';
            }
        },
    });

    const rtl = computed({
        get: () => textDirection.value == 'ltr',
        set: (newValue) => {
            if (newValue) {
                textDirection.value = 'ltr';
            } else {
                textDirection.value = 'rtl';
            }
        },
    });

    const menuMode = useStorage<MenuMode>('app-layout-mode', defaultMenuMode);

    function computedMenuModePredicate(
        predicateMode: MenuMode
    ): WritableComputedRef<boolean> {
        return computed({
            get: () => menuMode.value === predicateMode,
            set: (newValue) => {
                if (newValue) {
                    menuMode.value = predicateMode;
                } else if (menuMode.value === predicateMode) {
                    menuMode.value = defaultMenuMode.value;
                }
            },
        });
    }

    const staticMode = computedMenuModePredicate('static');
    const overlayMode = computedMenuModePredicate('overlay');
    const slimMode = computedMenuModePredicate('slim');
    const slimPlusMode = computedMenuModePredicate('slim-plus');
    const horizontalMode = computedMenuModePredicate('horizontal');
    const revealMode = computedMenuModePredicate('reveal');
    const drawerMode = computedMenuModePredicate('drawer');

    const sidebarAnchored = useStorage<boolean>(
        'app-layout-sidebar-anchored',
        defaultSidebarAnchored
    );

    function toggleSidebarAnchored() {
        sidebarAnchored.value = !sidebarAnchored.value;
    }

    const internalScale = useStorage<number>('app-layout-scale', defaultScale);

    const scale = computed({
        get: () =>
            Math.max(
                minScale.value,
                Math.min(internalScale.value, maxScale.value)
            ),
        set: setScale,
    });

    function setScale(newValue: number) {
        internalScale.value = Math.max(
            minScale.value,
            Math.min(newValue, maxScale.value)
        );
    }

    watch(
        scale,
        () => {
            document.documentElement.style.fontSize = `${scale.value}px`;
        },
        { immediate: true }
    );

    function increaseScale() {
        scale.value += scaleStep.value;
    }

    function decreaseScale() {
        scale.value -= scaleStep.value;
    }

    const scales = computed(() =>
        range(minScale.value, maxScale.value + scaleStep.value, scaleStep.value)
    );

    const { scheme: colorScheme } = useColorScheme();

    const transparentTopbar = useStorage<boolean>(
        'app-layout-transparent-topbar',
        defaultTransparentTopbar
    );

    const solidTopbar = computed({
        get: () => !transparentTopbar.value,
        set: (newValue) => (transparentTopbar.value = !newValue),
    });

    function toggleTransparentTopbar() {
        transparentTopbar.value = !transparentTopbar.value;
    }

    const menuTheme = computed<ColorScheme>(() => colorScheme.value);
    const topbarTheme = computed<ColorScheme | 'transparent'>(() =>
        transparentTopbar.value ? 'transparent' : colorScheme.value
    );

    const ripple = useStorage<boolean>('app-layout-ripple', defaultRipple);

    function toggleRipple() {
        ripple.value = !ripple.value;
    }

    const primeVue = ref<ReturnType<typeof usePrimeVue>>();

    watch(
        [ripple, primeVue],
        ([newValue, primeVue]) => {
            if (primeVue) {
                primeVue.config.ripple = newValue;
            }
        },
        { immediate: true }
    );

    const inputStyle = useStorage<InputStyle>(
        'app-layout-input-style',
        defaultInputStyle
    );

    watch(
        [inputStyle, primeVue],
        ([newValue, primeVue]) => {
            if (primeVue) {
                primeVue.config.inputStyle = newValue;
            }
        },
        { immediate: true }
    );

    function toggleInputStyle() {
        if (inputStyle.value === 'outlined') {
            inputStyle.value = 'filled';
        } else {
            inputStyle.value = 'outlined';
        }
    }

    const internalConfigTogglePosition = useStorage<ConfigTogglePosition>(
        'app-config-toggle-position',
        defaultConfigTogglePosition
    );

    const configEnabled = computed(() => userConfigItems.value.length > 0);
    const configTogglePosition = computed<ConfigTogglePosition>({
        get: () =>
            configEnabled.value ? internalConfigTogglePosition.value : 'none',
        set: (newValue) => {
            internalConfigTogglePosition.value = newValue;
        },
    });

    const topbarConfigToggle = computed<boolean>(
        () => configTogglePosition.value === 'topbar'
    );
    const floatingConfigToggle = computed<boolean>(
        () => configTogglePosition.value === 'floating'
    );

    const dismissableMask = ref<boolean>(receiverConfigStore.dismissableMask);
    const closeOnEscape = ref<boolean>(receiverConfigStore.closeOnEscape);

    // Watch for changes in internal refs and update the receiverUserConfigStore
    watch(dismissableMask, (newValue) => {
        receiverConfigStore.dismissableMask = newValue;
    });

    watch(closeOnEscape, (newValue) => {
        receiverConfigStore.closeOnEscape = newValue;
    });

    // Watch for changes in the receiverUserConfigStore and update the internal refs
    receiverConfigStore.$subscribe((_, state) => {
        dismissableMask.value = state.dismissableMask;
        closeOnEscape.value = state.closeOnEscape;
    });

    return {
        ltr,
        rtl,
        ripple,
        inputStyle,
        menuMode,
        staticMode,
        overlayMode,
        slimMode,
        slimPlusMode,
        horizontalMode,
        revealMode,
        drawerMode,
        menuModes,
        dismissableMask,
        closeOnEscape,

        toggleRipple,
        toggleInputStyle,

        sidebarAnchored,
        toggleSidebarAnchored,
        hideSidebarTimeout,

        scale,
        setScale,
        defaultScale,
        minScale,
        maxScale,
        scaleStep,
        increaseScale,
        decreaseScale,
        scales,

        transparentTopbar,
        solidTopbar,
        toggleTransparentTopbar,
        configure,
        colorScheme,
        menuTheme,
        topbarTheme,
        userConfigItems,
        configTogglePosition,
        configEnabled,
        topbarConfigToggle,
        floatingConfigToggle,
        primeVue,
    };
});
